import { startCase } from 'lodash-es'
import { useSubcategories } from '~/entities/categories'
import { extractInboxName, useInboxNames } from '~/entities/data-sources'
import { usePayors } from '~/entities/payors'
import { getDisplayName, useReviewers } from '~/entities/reviewer'
import { useUserStore } from '~/entities/user'
import { formatDateDefault } from '~/shared/lib/datetime'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { Tooltip } from '~/shared/ui'
import { useFilters, usePinnedFilters } from '../../model'
import { ClassificationFilter } from '../filters/items/classification-filter/classification-filter'
import { DateFilter } from '../filters/items/date-filter'
import { FlaggedFilter } from '../filters/items/flagged-filter'
import { PayorFilter } from '../filters/items/payor-filter/payor-filter'
import { PhaseFilter } from '../filters/items/phase-filter'
import { ReviewerFilter } from '../filters/items/reviewer-filter/reviewer-filter'
import { SourceFilter } from '../filters/items/source-filter'
import { StatusFilter } from '../filters/items/status-filter'
import { VisibilityFilter } from '../filters/items/visibility-filter'
import { FilterChip } from './filter-chip'

interface FilterChipsProps {
  mode: 'pinned' | 'unpinned'
}

export function FilterChips({ mode }: FilterChipsProps) {
  const { filters, setFilters } = useFilters()
  const { pinnedFilters } = usePinnedFilters()
  const isOnshore = useUserStore((state) => state.isOnshore)
  const isEobEnabled = useIsFeatureEnabled('2026-02-eob-phase-1')
  const {
    data: { reviewers },
  } = useReviewers()
  const { data: inboxNames } = useInboxNames()
  const {
    data: {
      data: { insurancePayors: payors },
    },
  } = usePayors()
  const selectedInbox = inboxNames.inbox_names.find((inboxName) => inboxName === filters.source)
  const { name: inboxName, description: inboxDescription } = extractInboxName(selectedInbox ?? '')
  const selectedPayorName = payors.find((p) => p.payorId === filters.payor)?.payorName
  const subcategories = useSubcategories()
  const selectedSubcategory = subcategories.find(
    (subcategory) => subcategory.label === filters.classification,
  )

  return (
    <>
      <FilterChip
        filterKey="status"
        label="Status"
        hasValue={filters.status.length > 0}
        onClose={() => setFilters({ status: [] })}
        dropdownContent={<StatusFilter />}
        pinned={pinnedFilters.includes('status')}
        mode={mode}
      >
        {filters.status[0] === 'awaiting'
          ? 'Awaiting / In Progress'
          : filters.status[0] === 'processing'
            ? 'Processing'
            : filters.status[0] === 'auto_reviewed'
              ? 'Auto Finished'
              : startCase(filters.status[0])}
      </FilterChip>

      <FilterChip
        filterKey="phase"
        label="Phase"
        hasValue={filters.phase.length > 0}
        onClose={() => setFilters({ phase: [] })}
        dropdownContent={<PhaseFilter />}
        pinned={pinnedFilters.includes('phase')}
        mode={mode}
      >
        {startCase(filters.phase[0])}
      </FilterChip>

      {isOnshore && (
        <FilterChip
          filterKey="visibility"
          label="Location"
          hasValue={filters.visibility !== ''}
          onClose={() => setFilters({ visibility: '' })}
          dropdownContent={<VisibilityFilter />}
          pinned={pinnedFilters.includes('visibility')}
          mode={mode}
        >
          US Based
        </FilterChip>
      )}

      {isEobEnabled && (
        <FilterChip
          filterKey="flagged"
          label="Flagged Status"
          hasValue={filters.flagged !== 'not_flagged'}
          onClose={() => setFilters({ flagged: 'not_flagged' })}
          dropdownContent={<FlaggedFilter />}
          pinned={pinnedFilters.includes('flagged')}
          mode={mode}
        >
          Flagged
        </FilterChip>
      )}

      <FilterChip
        filterKey="payor"
        label="Payor"
        hasValue={!!filters.payor}
        onClose={() => setFilters({ payor: '' })}
        dropdownContent={
          <div className="max-w-[255px]">
            <PayorFilter />
          </div>
        }
        pinned={pinnedFilters.includes('payor')}
        mode={mode}
      >
        {selectedPayorName ? (
          <Tooltip content={selectedPayorName}>
            <span>Payor: {filters.payor}</span>
          </Tooltip>
        ) : (
          `Payor: ${filters.payor}`
        )}
      </FilterChip>

      <FilterChip
        filterKey="classification"
        label="Classification"
        hasValue={filters.classification !== ''}
        onClose={() => setFilters({ classification: '' })}
        dropdownContent={<ClassificationFilter />}
        pinned={pinnedFilters.includes('classification')}
        mode={mode}
      >
        {selectedSubcategory?.displayLabel}
      </FilterChip>

      <FilterChip
        filterKey="reviewer"
        label="Reviewer"
        hasValue={!!filters.reviewer}
        onClose={() => setFilters({ reviewer: '' })}
        dropdownContent={
          <div className="max-w-[255px]">
            <ReviewerFilter />
          </div>
        }
        pinned={pinnedFilters.includes('reviewer')}
        mode={mode}
      >
        {getDisplayName(reviewers.find((reviewer) => reviewer.sub === filters.reviewer))}
      </FilterChip>

      <FilterChip
        filterKey="source"
        label="Source"
        hasValue={!!filters.source && !!selectedInbox}
        onClose={() => setFilters({ source: '' })}
        dropdownContent={
          <div className="max-w-[255px]">
            <SourceFilter />
          </div>
        }
        pinned={pinnedFilters.includes('source')}
        mode={mode}
      >
        {inboxName}: {inboxDescription}
      </FilterChip>

      <FilterChip
        filterKey="date"
        label="Date Received"
        hasValue={!!filters.dateFrom && !!filters.dateTo}
        onClose={() => setFilters({ dateFrom: '', dateTo: '' })}
        dropdownContent={<DateFilter />}
        pinned={pinnedFilters.includes('date')}
        mode={mode}
      >
        {filters.dateFrom === filters.dateTo
          ? formatDateDefault(filters.dateFrom)
          : `${formatDateDefault(filters.dateFrom)} - ${formatDateDefault(filters.dateTo)}`}
      </FilterChip>
    </>
  )
}
